import {
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Table,
  Tag,
  Icon,
  Button,
} from '@dev-spendesk/grapes';
import { useState, type ReactNode } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { CompareExportIllustration } from './CompareExportIllustration';
import { Step } from './Step';
import { getFieldContext } from './helpers';

export const CompareExportSettings = ({
  actions,
  exportLevel,
  fieldType,
}: {
  actions: ReactNode;
  exportLevel: 'header' | 'headerLine';
  fieldType: 'customField' | 'costCenter' | 'expenseCategory';
}) => {
  const { t } = useTranslation('global');
  const [selectTab, setSelectTab] = useState<'current' | 'recommended'>(
    'current',
  );
  const { fieldName, fieldNamePlural, line1, line2 } = getFieldContext(
    fieldType,
    exportLevel,
    t,
  );

  return (
    <Step
      illustration={
        <CompareExportIllustration
          fieldType={fieldType}
          exportLevel={exportLevel}
          selectedTab={selectTab}
        />
      }
      actions={actions}
    >
      <h1 className="heading-m">
        {t(
          'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.title',
        )}
      </h1>

      <Tabs
        onChange={(index) =>
          setSelectTab(index === 0 ? 'current' : 'recommended')
        }
      >
        <TabList className="mt-32 mb-16">
          <Tab>
            {t(
              'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.current.tab',
            )}
          </Tab>
          <Tab>
            {t(
              'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.recommended.tab',
            )}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <h2 className="title-m mt-32">
              {t(
                'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.current.h1',
              )}
            </h2>

            <ul className="text-content-secondary-bg-primary my-16 list-disc pl-24">
              <li>
                {t(
                  'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.current.h1l1',
                  { fieldName },
                )}
              </li>
              <li>
                {exportLevel === 'header'
                  ? t(
                      'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.current.h1l2',
                      { fieldName },
                    )
                  : t(
                      'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.headerLine.current.h1l2',
                      { fieldName, fieldNamePlural },
                    )}
              </li>
            </ul>

            <Table.Mini
              columns={[
                {
                  id: 'label',
                  renderCell: (v) => (
                    <p className="text-content-primary">{v.label}</p>
                  ),
                },
                { id: 'value', renderCell: (v) => v.value },
              ]}
              data={[
                {
                  label: t(
                    'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.example.line1',
                    { fieldName },
                  ),
                  value: <Tag variant="purple">{line1}</Tag>,
                },
                {
                  label: t(
                    'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.example.line2',
                    { fieldName },
                  ),
                  value: <Tag>{line2}</Tag>,
                },
              ]}
              getRowId={(row) => row.label}
            />

            {exportLevel === 'header' ? (
              <HeaderCurrentExplanationBlock
                line1={line1}
                line2={line2}
                fieldName={fieldName}
                fieldNamePlural={fieldNamePlural}
              />
            ) : (
              <HeaderLineCurrentExplanationBlock
                line1={line1}
                line2={line2}
                fieldName={fieldName}
                fieldNamePlural={fieldNamePlural}
              />
            )}
          </TabPanel>
          <TabPanel>
            <h2 className="title-m mt-32">
              {exportLevel === 'header'
                ? t(
                    'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.recommended.h1',
                    { fieldName },
                  )
                : t(
                    'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.headerLine.recommended.h1',
                    { fieldName },
                  )}
            </h2>

            <p className="text-content-secondary-bg-primary my-16">
              {exportLevel === 'header'
                ? t(
                    'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.recommended.h1l1',
                    { fieldName },
                  )
                : t(
                    'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.headerLine.recommended.h1l1',
                    { fieldName },
                  )}
            </p>

            <Table.Mini
              columns={[
                {
                  id: 'label',
                  renderCell: (v) => (
                    <p className="text-content-primary">{v.label}</p>
                  ),
                },
                { id: 'value', renderCell: (v) => v.value },
              ]}
              data={[
                {
                  label: t(
                    'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.example.line1',
                    { fieldName },
                  ),
                  value: <Tag variant="purple">{line1}</Tag>,
                },
                {
                  label: t(
                    'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.example.line2',
                    { fieldName },
                  ),
                  value: <Tag>{line2}</Tag>,
                },
              ]}
              getRowId={(row) => row.label}
            />

            {exportLevel === 'header' ? (
              <HeaderRecommendedExplanationBlock
                line1={line1}
                line2={line2}
                fieldNamePlural={fieldNamePlural}
              />
            ) : (
              <HeaderLineRecommendedExplanationBlock
                line1={line1}
                line2={line2}
                fieldNamePlural={fieldNamePlural}
              />
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Button
        variant="secondaryNeutral"
        component="a"
        href="https://spendesk.com/"
        target="_blank"
        rel="noopener"
        iconName="arrow-top-right-square"
        iconPosition="right"
        text={t('misc.helpCenterArticle')}
      />
    </Step>
  );
};

export const HeaderCurrentExplanationBlock = ({
  line1,
  line2,
  fieldName,
  fieldNamePlural,
}: {
  line1: string;
  line2: string;
  fieldName: string;
  fieldNamePlural: string;
}) => {
  const { t } = useTranslation('global');
  return (
    <>
      <h2 className="title-m mt-32 flex flex-wrap items-center gap-8">
        {t(
          'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.current.h2',
        )}
        <Icon name="arrow-right" />
        <Tag variant="purple">{line1}</Tag>
      </h2>

      <p className="text-content-secondary-bg-primary my-16">
        {t(
          'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.current.h2l1',
          { fieldName },
        )}
      </p>

      <h2 className="title-m mt-32 flex flex-wrap items-center gap-8">
        {t(
          'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.current.h3',
        )}
        <Icon name="arrow-right" />
        <Tag>{line2}</Tag>
      </h2>

      <p className="text-content-secondary-bg-primary my-16">
        {t(
          'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.current.h3l1',
          { fieldNamePlural },
        )}
      </p>
    </>
  );
};

export const HeaderLineCurrentExplanationBlock = ({
  line1,
  line2,
  fieldName,
  fieldNamePlural,
}: {
  line1: string;
  line2: string;
  fieldName: string;
  fieldNamePlural: string;
}) => {
  const { t } = useTranslation('global');
  return (
    <>
      <h2 className="title-m mt-32 flex flex-wrap items-center gap-8">
        {t(
          'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.headerLine.current.h2',
        )}
        <Icon name="arrow-right" />
        <Tag variant="purple">{line1}</Tag>
      </h2>

      <p className="text-content-secondary-bg-primary my-16">
        {t(
          'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.headerLine.current.h2l1',
          { fieldName },
        )}
      </p>

      <h2 className="title-m mt-32 flex flex-wrap items-center gap-8">
        {t(
          'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.headerLine.current.h3',
        )}
        <Icon name="arrow-right" />
        <Tag variant="purple">{line1}</Tag>
        <Tag>{line2}</Tag>
      </h2>

      <p className="text-content-secondary-bg-primary my-16">
        {t(
          'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.headerLine.current.h3l1',
          { fieldNamePlural },
        )}
      </p>
    </>
  );
};

export const HeaderRecommendedExplanationBlock = ({
  line1,
  line2,
  fieldNamePlural,
}: {
  line1: string;
  line2: string;
  fieldNamePlural: string;
}) => {
  const { t } = useTranslation('global');

  return (
    <>
      <h2 className="title-m mt-32 flex flex-wrap items-center gap-8">
        {t(
          'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.current.h2',
        )}
        <Icon name="arrow-right" />
        <Tag variant="purple">{line1}</Tag>
        <Tag>{line2}</Tag>
      </h2>

      <p className="text-content-secondary-bg-primary my-16">
        {t(
          'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.recommended.h2l1',
          { fieldNamePlural },
        )}
      </p>
    </>
  );
};

export const HeaderLineRecommendedExplanationBlock = ({
  line1,
  line2,
  fieldNamePlural,
}: {
  line1: string;
  line2: string;
  fieldNamePlural: string;
}) => {
  const { t } = useTranslation('global');

  return (
    <>
      <h2 className="title-m mt-32 flex flex-wrap items-center gap-8">
        {t(
          'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.headerLine.current.h2',
        )}
        <Icon name="arrow-right" />
        <Tag>
          <Icon name="circle-strikeout" />
          {t(
            'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.headerLine.recommended.h2label',
          )}
        </Tag>
      </h2>

      <h2 className="title-m mt-32 flex flex-wrap items-center gap-8">
        {t(
          'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.headerLine.current.h3',
        )}
        <Icon name="arrow-right" />
        <Tag variant="purple">{line1}</Tag>
        <Tag>{line2}</Tag>
      </h2>

      <p className="text-content-secondary-bg-primary my-16">
        {t(
          'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.headerLine.recommended.h3l1',
          { fieldNamePlural },
        )}
      </p>
    </>
  );
};
